import type { ReactNode } from 'react';

const PHONE_WIDTH = 320;
const PHONE_HEIGHT = 640;

interface PhoneFrameProps {
  children: ReactNode;
  /** Inner screen background (CSS color string). */
  bgcolor: string;
  /**
   * When true, the inner screen scrolls vertically so the whole post (media +
   * caption + chrome) can exceed the fixed frame height instead of being
   * compressed. Feed-style previews (Instagram, Website, LinkedIn, X) opt in;
   * TikTok keeps the default full-bleed overlay (no whole-screen scroll).
   */
  scrollable?: boolean;
}

export const PhoneFrame = ({ children, bgcolor, scrollable = false }: PhoneFrameProps) => (
  <div
    className="mx-auto p-1.5 rounded-3xl bg-[#0b0b0b] shadow-[0_8px_24px_rgba(0,0,0,0.16)]"
    style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }}
  >
    <div
      data-testid="phone-screen"
      className={`relative flex flex-col w-full h-full rounded-[20px] text-neutral-950 ${
        scrollable ? 'overflow-y-auto [scrollbar-width:thin]' : 'overflow-hidden'
      }`}
      style={{ backgroundColor: bgcolor }}
    >
      {children}
    </div>
  </div>
);

PhoneFrame.displayName = 'PhoneFrame';

interface StatusBarProps {
  /** Light = white text/icons (dark backgrounds); dark = black text/icons (light backgrounds). */
  tone: 'light' | 'dark';
}

const Indicators = ({ color }: { color: string }) => (
  <div className="flex items-center gap-1.5" style={{ color }}>
    {/* eslint-disable-next-line i18next/no-literal-string -- decorative signal-strength dots in the mock phone status bar, not words */}
    <span className="text-[11px] font-semibold tracking-[0.5px]">●●●●</span>
    <svg viewBox="0 0 16 12" width={16} height={12} fill={color} aria-hidden>
      <path d="M8 2.5C5.2 2.5 2.6 3.5 0.7 5.1L8 12L15.3 5.1C13.4 3.5 10.8 2.5 8 2.5Z" />
    </svg>
    <svg viewBox="0 0 22 10" width={22} height={10} fill={color} aria-hidden>
      <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke={color} fill="none" />
      <rect x="2" y="2" width="15" height="6" rx="1" />
      <rect x="19.5" y="3" width="2" height="4" rx="0.5" />
    </svg>
  </div>
);

export const StatusBar = ({ tone }: StatusBarProps) => {
  const color = tone === 'light' ? '#fff' : '#000';
  return (
    <div className="relative flex items-center justify-between px-4 pt-1.5 pb-1">
      <span className="text-[13px] font-semibold min-w-10" style={{ color }}>
        9:41
      </span>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] h-[22px] bg-black rounded-b-2xl" />
      <Indicators color={color} />
    </div>
  );
};

StatusBar.displayName = 'StatusBar';
